// Extracted from web/components/WorkbenchDialogs.tsx; behavior and declaration bodies preserved.
import { useState } from "react";
import { ArrowRight, WandSparkles } from "lucide-react";

import type { Diagnostic, ModelDocument, NamingRule, Operation } from "../../../shared/contracts";

import { previewNaming, uid } from "../../../core";

import { Button, Diagnostics, Field, Modal, Notice, errorText } from "../../components/Primitives";

export function NamingDialog({
  document,
  onClose,
  onOperations,
}: {
  document: ModelDocument;
  onClose: () => void;
  onOperations: (ops: Operation[], summary: string) => void;
}) {
  const [rule, setRule] = useState<NamingRule>({
    ruleSetId: uid(),
    name: "Workspace naming rule",
    version: 1,
    case: "snake_case",
    tablePrefix: "",
    columnPrefix: "",
    surrogateSuffix: "_id",
    businessSuffix: "_code",
    abbreviations: {},
    maxLength: 63,
  });
  const [preview, setPreview] = useState<any>();
  const [error, setError] = useState("");
  const update = (c: Partial<NamingRule>) => {
    setRule({ ...rule, ...c });
    setPreview(undefined);
  };
  return (
    <Modal
      title="Naming policy"
      subtitle="Preview explicit renames. Unrelated edits will never rename your model."
      onClose={onClose}
      wide
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          {preview ? (
            <Button
              variant="primary"
              disabled={
                !preview.operations.length ||
                preview.diagnostics.some(
                  (d: Diagnostic) => d.severity === "error",
                )
              }
              onClick={() => {
                onOperations(preview.operations, `Apply ${rule.name}`);
                onClose();
              }}
            >
              Apply {preview.operations.length} changes
            </Button>
          ) : (
            <Button
              variant="primary"
              icon={WandSparkles}
              onClick={() => {
                try {
                  setPreview(previewNaming(document, rule));
                  setError("");
                } catch (e) {
                  setError(errorText(e));
                }
              }}
            >
              Preview renames
            </Button>
          )}
        </>
      }
    >
      <Field label="Rule name">
        <input
          value={rule.name}
          onChange={(e) => update({ name: e.target.value })}
        />
      </Field>
      <div className="form-row">
        <Field label="Case convention">
          <select
            value={rule.case}
            onChange={(e) => update({ case: e.target.value as any })}
          >
            {[
              "preserve",
              "snake_case",
              "camelCase",
              "PascalCase",
              "UPPER_SNAKE",
              "Title Case",
            ].map((v) => (
              <option key={v}>{v}</option>
            ))}
          </select>
        </Field>
        <Field label="Maximum identifier length">
          <input
            type="number"
            min={1}
            max={256}
            value={rule.maxLength}
            onChange={(e) => update({ maxLength: Number(e.target.value) })}
          />
        </Field>
      </div>
      <div className="form-row">
        <Field label="Table prefix">
          <input
            value={rule.tablePrefix}
            onChange={(e) => update({ tablePrefix: e.target.value })}
          />
        </Field>
        <Field label="Column prefix">
          <input
            value={rule.columnPrefix}
            onChange={(e) => update({ columnPrefix: e.target.value })}
          />
        </Field>
      </div>
      <div className="form-row">
        <Field label="Surrogate-key suffix">
          <input
            value={rule.surrogateSuffix}
            onChange={(e) => update({ surrogateSuffix: e.target.value })}
          />
        </Field>
        <Field label="Business-key suffix">
          <input
            value={rule.businessSuffix}
            onChange={(e) => update({ businessSuffix: e.target.value })}
          />
        </Field>
      </div>
      {error && <Notice kind="error">{error}</Notice>}
      {preview && (
        <>
          <Diagnostics items={preview.diagnostics} />
          <div className="rename-table">
            {preview.operations.map((op: Operation, i: number) => (
              <div key={i}>
                <span>
                  {document.objects.find((o) => o.objectId === op.objectId)
                    ?.name || op.objectId}
                </span>
                <ArrowRight size={13} />
                <strong>{String(op.changes?.name || "Policy change")}</strong>
              </div>
            ))}
          </div>
        </>
      )}
    </Modal>
  );
}
